import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface HelpHintProps {
  kind?: 'kpi' | 'chart' | 'indicator';
  shortText: string;
  longText: string; // kept for API compatibility, but not shown per simplified design
  title?: string;
}

const HelpHint: React.FC<HelpHintProps> = ({ 
  kind = 'chart', 
  shortText, 
  longText, 
  title = 'Vad visar denna indikator?' 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const Modal = () => {
    if (!isOpen) return null;
    return createPortal(
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-[hsl(var(--background))]/60 dark:bg-black/60"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
        {/* Centered dialog */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-[min(90vw,28rem)] rounded-xl bg-card p-4 shadow-2xl ring-1 ring-border md:p-5"
          >
            {/* Close button */}
            <button
              aria-label="Stäng"
              className="absolute right-3 top-3 rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              onClick={() => setIsOpen(false)}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Content */}
            <h4 className="mb-2 text-sm font-semibold text-foreground">{title}</h4>
            <div className="text-sm text-foreground leading-relaxed">
              <div>
                <strong>Kort beskrivning:</strong>
                <p className="mt-1 text-muted-foreground">{shortText}</p>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <button
        aria-label="Visa hjälp"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
        onClick={() => setIsOpen(true)}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      <Modal />
    </>
  );
};

export default HelpHint;
